import { Tag } from 'antd';
import { useParams } from 'react-router';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { withinSection } from '../../../../../shared/components/section-trail';
import { MAINTENANCE_TAB_LABELS, maintenanceTabPath } from '../../../maintenance.paths';
import { tyreDetailFallback } from '../components/tyre-detail-fallback';
import { TyreActions } from '../components/TyreActions';
import { TyreFormDrawer } from '../components/TyreFormDrawer';
import { TyreHistory } from '../components/TyreHistory';
import { TyreLocationDiagram } from '../components/TyreLocationDiagram';
import { TyreMoveModal } from '../components/TyreMoveModal';
import { TyreSummary } from '../components/TyreSummary';
import { useCanManageTyres } from '../hooks/use-can-manage-tyres';
import { useTyreDetail } from '../hooks/use-tyre-detail';
import { useTyreDialogs } from '../hooks/use-tyre-dialogs';
import { useTyreMove } from '../hooks/use-tyre-move';
import { useTyreSave } from '../hooks/use-tyre-save';
import { TYRE_STATUS_COLORS, TYRE_STATUS_LABELS } from '../policies/tyre-labels';
import { tyreToFormValues } from '../policies/tyre-form.policy';

const TRAIL = withinSection('maintenance', { label: MAINTENANCE_TAB_LABELS.tyres, to: maintenanceTabPath('tyres') });

/** One tyre: its particulars, where it sits, its positions over time and its history, with the moves its status allows. `tyre.manage` shows the controls; the server decides. */
export function TyreDetailPage() {
  const { id = '' } = useParams();
  const { state, reload } = useTyreDetail(id);
  const move = useTyreMove(reload);
  const save = useTyreSave(reload);
  const canManage = useCanManageTyres();
  const dialogs = useTyreDialogs(move.resetError, save.resetError);

  const fallback = tyreDetailFallback(state, TRAIL);
  if (fallback) return fallback;
  if (state.status !== 'loaded') return null;

  const { tyre } = state;
  const subject = { id: tyre.id, serial: tyre.serial };
  return (
    <>
      <PageHeader
        breadcrumbs={TRAIL}
        title={tyre.serial}
        description={`${tyre.brand} ${tyre.size}`}
        actions={
          <>
            <Tag color={TYRE_STATUS_COLORS[tyre.status]}>{TYRE_STATUS_LABELS[tyre.status]}</Tag>
            {canManage && <TyreActions status={tyre.status} onMove={dialogs.openMove} onEdit={dialogs.openEdit} />}
          </>
        }
      />
      <div className="flex flex-col gap-8">
        <TyreSummary tyre={tyre} />
        {tyre.fitment && <TyreLocationDiagram fitment={tyre.fitment} />}
        <TyreHistory tyre={tyre} />
      </div>
      <TyreMoveModal
        kind={dialogs.kind}
        subject={subject}
        error={move.error}
        submitting={move.saving}
        onSubmit={(values) => (dialogs.kind ? move.run(dialogs.kind, subject, values) : Promise.resolve(false))}
        onClose={dialogs.closeMove}
      />
      <TyreFormDrawer
        open={dialogs.isEditing}
        editing={{ id: tyre.id, values: tyreToFormValues(tyre), supplierName: tyre.supplierName, brandName: tyre.brand, sizeName: tyre.size }}
        error={save.error}
        submitting={save.saving}
        onSubmit={(values) => save.update(tyre.id, values)}
        onClose={dialogs.closeEdit}
      />
    </>
  );
}
